import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CaretDown, CaretUp, CheckCircle, CircleNotch, PencilSimple, Prohibit, Scales, TrashSimple, WarningCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_TONE = {
  calculated: "bg-emerald-50 text-emerald-700 border-emerald-200",
  entered: "bg-blue-50 text-blue-700 border-blue-200",
  estimated: "bg-amber-50 text-amber-700 border-amber-300",
};
const STATUS_TONE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending_approval: "bg-amber-50 text-amber-700 border-amber-300",
  rejected: "bg-red-50 text-red-700 border-red-200",
  not_required: "bg-slate-100 text-slate-500 border-slate-200",
};
const STATUS_LABEL = { approved: "Approved", pending_approval: "Pending approval", rejected: "Rejected", not_required: "Calculated — no approval needed" };
const Chip = ({ tone, children, testid }) => (
  <span data-testid={testid} className={`inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${tone}`}>{children}</span>
);

const RankRangeBar = ({ c, total }) => {
  const pct = (r) => ((r - 1) / Math.max(total - 1, 1)) * 100;
  return (
    <div className="flex-1 relative h-4">
      <div className="absolute inset-y-1.5 left-0 right-0 bg-slate-100" />
      <div className="absolute inset-y-1 bg-slate-300"
        style={{ left: `${pct(c.rank_low)}%`, width: `${Math.max(pct(c.rank_high) - pct(c.rank_low), 1.5)}%` }} />
      <div className="absolute top-0 h-4 w-1 bg-slate-900" style={{ left: `calc(${pct(c.base_rank)}% - 2px)` }} />
    </div>
  );
};

function DimRow({ pc, d, productId, isAdmin, refresh }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ value: d.value, source: "", method: "", confidence: 70 });
  const [busy, setBusy] = useState(null);
  const isCommercial = d.assumption_status !== "not_required";

  const save = async () => {
    setBusy("save");
    try {
      await axios.put(`${API}/prioritization/${productId}/${pc.id}/override/${d.dimension}`, form);
      toast.success(`${d.label} entered value saved — approval reset to pending`);
      setEditing(false); refresh();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed"); }
    finally { setBusy(null); }
  };
  const clear = async () => {
    setBusy("clear");
    try {
      await axios.delete(`${API}/prioritization/${productId}/${pc.id}/override/${d.dimension}`);
      toast.success("Entered value removed — reverted to the run value"); refresh();
    } catch { toast.error("Remove failed"); }
    finally { setBusy(null); }
  };
  const review = async (decision) => {
    setBusy(decision);
    try {
      await axios.post(`${API}/prioritization/${productId}/${pc.id}/assumption/${d.dimension}/review`, { decision });
      toast.success(`${d.label} for ${pc.name}: ${decision}`); refresh();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Review failed"); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid={`prov-dim-${pc.id}-${d.dimension}`} className="px-4 py-2.5 border-t border-slate-100">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-xs font-semibold text-slate-800 w-40 shrink-0">{d.label}</p>
        <span className="font-mono-data text-sm font-bold text-slate-900 w-10">{d.value}</span>
        <Chip tone={SOURCE_TONE[d.source_type]} testid={`prov-source-type-${pc.id}-${d.dimension}`}>{d.source_type}</Chip>
        <Chip tone={STATUS_TONE[d.assumption_status]}>{STATUS_LABEL[d.assumption_status]}</Chip>
        <span className="text-[10px] text-slate-400 font-mono-data">conf {d.confidence} · ±{d.uncertainty_half_width}</span>
        <span className="ml-auto flex items-center gap-1.5">
          {d.source_type === "entered" && (
            <button data-testid={`prov-clear-${pc.id}-${d.dimension}`} onClick={clear} disabled={busy}
              className="text-[10px] text-slate-500 border border-slate-300 px-2 py-1 hover:bg-slate-50 flex items-center gap-1">
              {busy === "clear" ? <CircleNotch size={10} className="animate-spin" /> : <TrashSimple size={10} />} Remove entered value
            </button>
          )}
          {isCommercial && (
            <button data-testid={`prov-edit-${pc.id}-${d.dimension}`} onClick={() => setEditing(!editing)}
              className="text-[10px] text-slate-600 border border-slate-300 px-2 py-1 hover:bg-slate-50 flex items-center gap-1">
              <PencilSimple size={10} /> Enter value
            </button>
          )}
          {isAdmin && isCommercial && d.assumption_status !== "approved" && (
            <button data-testid={`prov-approve-${pc.id}-${d.dimension}`} onClick={() => review("approved")} disabled={busy}
              className="text-[10px] font-semibold text-white bg-emerald-700 px-2 py-1 hover:bg-emerald-800 flex items-center gap-1">
              {busy === "approved" ? <CircleNotch size={10} className="animate-spin" /> : <CheckCircle size={10} />} Approve
            </button>
          )}
          {isAdmin && isCommercial && d.assumption_status !== "rejected" && (
            <button data-testid={`prov-reject-${pc.id}-${d.dimension}`} onClick={() => review("rejected")} disabled={busy}
              className="text-[10px] text-red-700 border border-red-300 px-2 py-1 hover:bg-red-50 flex items-center gap-1">
              <Prohibit size={10} /> Reject
            </button>
          )}
        </span>
      </div>
      <p className="text-[11px] text-slate-500 mt-1"><span className="font-semibold text-slate-600">Source:</span> {d.source}</p>
      <p className="text-[10px] text-slate-400 mt-0.5">
        <span className="font-semibold">Method:</span> {d.method} · <span className="font-semibold">Data date:</span> {d.data_date ? new Date(d.data_date).toLocaleDateString() : "—"}
        {d.reviewer && <> · <span className="font-semibold">Reviewer:</span> {d.reviewer}</>}
        {d.entered_by && <> · <span className="font-semibold">Entered by:</span> {d.entered_by}</>}
      </p>
      {d.review_note && <p className="text-[10px] text-amber-700 mt-0.5">{d.review_note}</p>}
      {editing && (
        <div data-testid={`prov-edit-form-${pc.id}-${d.dimension}`} className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2 bg-blue-50/40 border border-blue-200 p-2.5">
          <input data-testid="override-value" type="number" min="0" max="100" className="h-7 text-xs px-2 border border-slate-300" placeholder="Value 0-100"
            value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
          <input data-testid="override-source" className="h-7 text-xs px-2 border border-slate-300 md:col-span-2" placeholder="Source (required — who/what provided it)"
            value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
          <input data-testid="override-method" className="h-7 text-xs px-2 border border-slate-300" placeholder="Method (required)"
            value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} />
          <div className="flex gap-1.5">
            <input data-testid="override-confidence" type="number" min="0" max="100" className="h-7 text-xs px-2 border border-slate-300 w-16" placeholder="Conf"
              value={form.confidence} onChange={(e) => setForm({ ...form, confidence: e.target.value })} />
            <button data-testid="override-save" onClick={save} disabled={busy}
              className="text-[10px] font-semibold text-white bg-slate-900 px-2.5 hover:bg-slate-800">
              {busy === "save" ? <CircleNotch size={10} className="animate-spin" /> : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CountryProvenance({ pc, productId, isAdmin, refresh }) {
  const [open, setOpen] = useState(false);
  const pending = pc.dimensions.filter((d) => d.assumption_status === "pending_approval").length;
  return (
    <div data-testid={`prov-country-${pc.id}`} className="bg-white border border-slate-200">
      <button onClick={() => setOpen(!open)} className="w-full px-4 py-2.5 flex items-center gap-3 text-left hover:bg-slate-50/60">
        <span className="font-heading font-bold text-sm text-slate-300 w-7">#{pc.rank}</span>
        <p className="text-xs font-semibold text-slate-900 flex-1">{pc.flag} {pc.name}</p>
        {pending > 0
          ? <Chip tone={STATUS_TONE.pending_approval}>{pending} pending approval</Chip>
          : <Chip tone={STATUS_TONE.approved}>All assumptions reviewed</Chip>}
        {open ? <CaretUp size={13} className="text-slate-400" /> : <CaretDown size={13} className="text-slate-400" />}
      </button>
      {open && pc.dimensions.map((d) => (
        <DimRow key={d.dimension} pc={pc} d={d} productId={productId} isAdmin={isAdmin} refresh={refresh} />
      ))}
    </div>
  );
}

export const PrioritizationUncertainty = ({ productId, isAdmin }) => {
  const [prov, setProv] = useState(null);
  const [sens, setSens] = useState(null);
  const [recon, setRecon] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [p, s, r] = await Promise.all([
        axios.get(`${API}/prioritization/${productId}/provenance`),
        axios.get(`${API}/prioritization/${productId}/sensitivity`),
        axios.get(`${API}/prioritization/${productId}/reconciliation`),
      ]);
      setProv(p.data); setSens(s.data); setRecon(r.data);
    } catch (e) { setError(e.response?.data?.detail || "Could not load uncertainty analysis"); }
    finally { setLoading(false); }
  }, [productId]);
  useEffect(() => { setLoading(true); load(); }, [load]);

  if (loading) return <div data-testid="uncertainty-loading" className="py-10 text-center"><CircleNotch size={18} className="animate-spin inline text-slate-400" /><p className="text-xs text-slate-400 mt-2">Computing rank ranges, provenance and sensitivity…</p></div>;
  if (error) return (
    <div data-testid="uncertainty-error" className="bg-white border border-red-200 px-5 py-4 text-xs text-red-700 flex items-center gap-2">
      <WarningCircle size={14} /> {String(error)} <button onClick={() => { setLoading(true); load(); }} className="ml-auto border border-slate-300 text-slate-600 px-2.5 py-1 hover:bg-slate-50">Retry</button>
    </div>
  );
  if (!sens || sens.status !== "ok") return null;
  const total = sens.countries.length;
  const flips = sens.weight_scenarios.filter((s) => s.top_market_id !== sens.countries[0]?.id);

  return (
    <>
      {/* ---- Rank ranges & sensitivity ---- */}
      <section data-testid="sensitivity-panel" className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200">
          <p className="font-heading font-bold text-sm tracking-tight flex items-center gap-2"><Scales size={15} /> Rank Ranges & Sensitivity</p>
          <p className="text-[11px] text-slate-500 mt-1">{sens.method}</p>
          <p data-testid="sensitivity-robustness" className="text-xs mt-2">
            {sens.top_market_robust
              ? <span className="text-emerald-700 font-semibold">Top market ({sens.base_top_market}) holds in all {sens.weight_scenarios.length} weight scenarios.</span>
              : <span className="text-amber-700 font-semibold">Top market changes under {flips.length} of {sens.weight_scenarios.length} weight scenarios — alternatives: {sens.alternative_top_markets.join(", ")}.</span>}
          </p>
        </div>
        <div className="divide-y divide-slate-100">
          {sens.countries.map((c) => (
            <div key={c.id} data-testid={`rank-range-${c.id}`} className="px-5 py-2.5 flex items-center gap-3">
              <p className="text-xs font-semibold text-slate-800 w-44 shrink-0 truncate">{c.flag} {c.name}</p>
              <span data-testid={`rank-range-label-${c.id}`} className="font-mono-data text-xs font-bold text-slate-900 w-20 shrink-0">
                {c.rank_low === c.rank_high ? `#${c.rank_low}` : `#${c.rank_low}–#${c.rank_high}`}
              </span>
              <RankRangeBar c={c} total={total} />
              <span className="font-mono-data text-[10px] text-slate-400 w-28 text-right shrink-0">score {c.composite_high}–{c.composite_low}</span>
              {c.rank_stable
                ? <Chip tone="bg-emerald-50 text-emerald-700 border-emerald-200">stable</Chip>
                : <Chip tone="bg-amber-50 text-amber-700 border-amber-300">±{c.rank_high - c.rank_low}</Chip>}
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-slate-100">
          <p className="overline-label text-slate-400">Weight scenarios (each dimension ±25%)</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {sens.weight_scenarios.map((s, i) => (
              <span key={i} data-testid={`weight-scenario-${s.dimension}-${i % 2}`}
                className={`text-[10px] px-2 py-1 border ${s.top_market_id !== sens.countries[0]?.id ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-500"}`}>
                {s.label} {s.direction} → top: {s.top_market} · {s.rank_changes} rank change(s)
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Input provenance & assumptions ---- */}
      {prov?.status === "ok" && (
        <section data-testid="provenance-panel" className="space-y-2">
          <div className="bg-white border border-slate-200 px-5 py-4">
            <p className="font-heading font-bold text-sm tracking-tight">Input Provenance & Assumptions</p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Chip tone={SOURCE_TONE.calculated}>calculated {prov.counts.calculated}</Chip>
              <Chip tone={SOURCE_TONE.entered}>entered {prov.counts.entered}</Chip>
              <Chip tone={SOURCE_TONE.estimated}>estimated {prov.counts.estimated}</Chip>
              <span data-testid="approval-summary" className={`text-xs font-semibold ml-2 ${prov.external_report_ready ? "text-emerald-700" : "text-amber-700"}`}>
                {prov.external_report_ready
                  ? "All commercial assumptions approved — ranking may appear in external reports."
                  : `${prov.approvals.pending_approval} of ${prov.approvals.total} commercial assumptions pending approval — the ranking is withheld from external reports until all are approved.`}
              </span>
            </div>
          </div>
          {prov.countries.map((pc) => (
            <CountryProvenance key={pc.id} pc={pc} productId={productId} isAdmin={isAdmin} refresh={load} />
          ))}
        </section>
      )}

      {/* ---- India vs Germany reconciliation ---- */}
      {recon?.status === "ok" && (
        <section data-testid="reconciliation-panel" className="bg-white border border-slate-200 px-5 py-4">
          <p className="font-heading font-bold text-sm tracking-tight">
            {recon.market_a.name} vs {recon.market_b.name} — two top markets, two decision questions
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <div className="border border-slate-200 p-3">
              <p className="overline-label text-slate-400">Composite priority lens — leader: {recon.composite_leader}</p>
              <p className="text-xs text-slate-600 leading-relaxed mt-1">{recon.decision_questions.composite_priority}</p>
            </div>
            <div className="border border-slate-200 p-3">
              <p className="overline-label text-slate-400">Attractiveness lens — leader: {recon.attractiveness_leader}</p>
              <p className="text-xs text-slate-600 leading-relaxed mt-1">{recon.decision_questions.market_attractiveness}</p>
            </div>
          </div>
          <table className="w-full text-[11px] border border-slate-200 mt-4">
            <thead><tr className="bg-slate-100">
              {["Dimension", "Weight", `${recon.market_a.name}`, `${recon.market_b.name}`, "Contribution Δ", "Advantage"].map((h) => (
                <th key={h} className="px-2.5 py-1.5 text-left font-semibold text-slate-700 border-b border-slate-200">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {recon.dimensions.map((d) => (
                <tr key={d.dimension} data-testid={`recon-dim-${d.dimension}`} className="border-b border-slate-100 last:border-0">
                  <td className="px-2.5 py-1.5 text-slate-700 font-semibold">{d.label}{d.inverted && <span className="text-slate-400 font-normal"> (inverted)</span>}</td>
                  <td className="px-2.5 py-1.5 text-slate-500">{d.weight}%</td>
                  <td className="px-2.5 py-1.5 font-mono-data">{d.a_value} <span className="text-slate-400">(+{d.a_contribution})</span></td>
                  <td className="px-2.5 py-1.5 font-mono-data">{d.b_value} <span className="text-slate-400">(+{d.b_contribution})</span></td>
                  <td className={`px-2.5 py-1.5 font-mono-data font-bold ${d.contribution_delta > 0 ? "text-emerald-700" : d.contribution_delta < 0 ? "text-red-700" : "text-slate-400"}`}>{d.contribution_delta > 0 ? "+" : ""}{d.contribution_delta}</td>
                  <td className="px-2.5 py-1.5 text-slate-600">{d.advantage}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-500 mt-3">
            <span className="font-semibold text-slate-700">Decisive dimensions:</span> {recon.decisive_dimensions.map((d) => `${d.label} (Δ${d.contribution_delta})`).join(" · ")}.
            {recon.crossover && <> <span className="font-semibold text-slate-700">Crossover:</span> {recon.attractiveness_leader} would overtake on the composite only if the Market Attractiveness weight rose from {recon.crossover.current_weight}% to {recon.crossover.required_weight} ({recon.crossover.share_of_total}% of total weight).</>}
          </p>
        </section>
      )}
    </>
  );
};
